/** Peças pequenas reaproveitadas em todas as telas. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { num } from '@dotapps/core';
import { Icon, type IconName } from './icons';

export function PageHead({
  eyebrow,
  title,
  lede,
  actions,
  className,
}: {
  eyebrow: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <section className={`page-head ${className ?? ''}`}>
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {lede && <p className="lede">{lede}</p>}
      </div>
      {actions && <div className="head-actions">{actions}</div>}
    </section>
  );
}

export function Panel({
  title,
  icon,
  action,
  children,
  className,
}: {
  title: ReactNode;
  icon?: IconName;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`panel ${className ?? ''}`}>
      <header className="panel-head">
        <h2>
          {icon && <Icon name={icon} />} {title}
        </h2>
        {action}
      </header>
      {children}
    </section>
  );
}

export function Avatar({ ini, size, title }: { ini: string; size?: 'xs' | 'sm'; title?: string }) {
  return (
    <span className={`av${size ? ` ${size}` : ''}`} title={title}>
      {ini || '?'}
    </span>
  );
}

export function Pill({
  children,
  tone,
  className,
}: {
  children: ReactNode;
  tone?: 'ok' | 'warn' | 'bad' | 'accent';
  className?: string;
}) {
  return <span className={`pill${tone ? ` pill-${tone}` : ''} ${className ?? ''}`}>{children}</span>;
}

export function Stars({ value }: { value: number }) {
  const n = Math.round(value);
  return (
    <>
      <span className="stars" title={String(value)}>
        {'★'.repeat(n)}
        <i>{'★'.repeat(Math.max(0, 5 - n))}</i>
      </span>{' '}
      {num(value, 1)}
    </>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function Loading({ label = 'Carregando…' }: { label?: string }) {
  return (
    <div className="loading-page" role="status">
      <span className="spinner" />
      <span>{label}</span>
    </div>
  );
}

export function Locked({ children }: { children: ReactNode }) {
  return (
    <p className="locked">
      <Icon name="lock" />
      {children}
    </p>
  );
}

/** Número que "conta" até o valor ao aparecer (respeita quem prefere menos movimento). */
export function CountUp({ value, format }: { value: number; format: (v: number) => string }) {
  const [shown, setShown] = useState(value);
  const from = useRef(0);
  useEffect(() => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setShown(value);
      return;
    }
    const start = performance.now();
    const a = from.current;
    let raf = 0;
    const step = (now: number) => {
      const k = Math.min(1, (now - start) / 900);
      const e = 1 - Math.pow(1 - k, 3);
      setShown(a + (value - a) * e);
      if (k < 1) raf = requestAnimationFrame(step);
      else from.current = value;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{format(shown)}</>;
}

/** Animação de "pensando" da IA. */
export function Thinking({ children }: { children: ReactNode }) {
  return (
    <div className="ai-thinking">
      <i />
      <i />
      <i /> {children}
    </div>
  );
}
